import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    DatePicker,
    Space,
    Tag,
    Badge,
    message,
    Card,
    Empty,
    Avatar,
    Tooltip,
    Popconfirm,
    AutoComplete,
} from "antd";
import {
    PlusOutlined,
    TeamOutlined,
    LockFilled,
    UnlockOutlined,
    CalendarOutlined,
    ReloadOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { api } from "../api";
import { fmtRange } from "../utils";
import ImageUpload from "../components/ImageUpload";

const { RangePicker } = DatePicker;

// NOTE: "FINISHED" was removed — it duplicated COMPLETED and isn't part of the
// mobile app's status enum, so events marked FINISHED vanished from the app's
// "ended" list. Existing FINISHED rows are still treated as completed by the
// backend; the option just can't be selected any more.
const STATUS_OPTIONS = ["UPCOMING", "ONGOING", "COMPLETED", "CANCELLED"].map(
    (s) => ({ value: s, label: s })
);

// Categories match the mobile app's filter chips exactly, so events created here
// show up under the right chip in the app.
const CATEGORY_OPTIONS = ["Համերգ", "Սպորտ", "Ցուցահանդես", "Թատրոն", "Այլ"].map(
    (c) => ({ value: c, label: c })
);

// Language codes match the app (it renders a flag per code).
const LANGUAGE_OPTIONS = [
    { value: "AM", label: "Հայերեն (AM)" },
    { value: "RU", label: "Русский (RU)" },
    { value: "EN", label: "English (EN)" },
    { value: "FR", label: "Français (FR)" },
    { value: "DE", label: "Deutsch (DE)" },
    { value: "ES", label: "Español (ES)" },
    { value: "IT", label: "Italiano (IT)" },
];

// Preset dress codes — the field is free-text too, so admins can pick or type.
const DRESS_CODE_OPTIONS = [
    "Comfortable clothes",
    "Casual",
    "Smart casual",
    "Formal",
    "Sportswear",
    "Volunteer T-shirt",
].map((d) => ({ value: d }));

const statusColor = (s) => {
    switch (s) {
        case "ONGOING":
            return "green";
        case "UPCOMING":
            return "blue";
        case "COMPLETED":
        case "FINISHED":
            return "default";
        case "CANCELLED":
            return "red";
        default:
            return "purple";
    }
};

const idOf = (e) =>
    e && typeof e === "object" ? String(e.userId || e._id || e.id) : String(e);

const EventsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [attendees, setAttendees] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [form] = Form.useForm();

    const [fStatus, setFStatus] = useState();
    const [fCategory, setFCategory] = useState();
    const [fRange, setFRange] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listEvents();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const categoryOptions = useMemo(() => {
        const set = new Set(items.map((e) => e.category).filter(Boolean));
        return [...set].map((c) => ({ value: c, label: c }));
    }, [items]);

    const filtered = useMemo(() => {
        return items.filter((e) => {
            if (fStatus && e.status !== fStatus) return false;
            if (fCategory && e.category !== fCategory) return false;
            if (fRange && fRange[0] && fRange[1]) {
                if (!e.startDate) return false;
                const d = dayjs(e.startDate);
                if (d.isBefore(fRange[0], "day") || d.isAfter(fRange[1], "day"))
                    return false;
            }
            return true;
        });
    }, [items, fStatus, fCategory, fRange]);

    const openEdit = (ev) => {
        setEditing(ev);
        form.setFieldsValue({
            ...ev,
            languages: (ev.languages || []).map(String),
            startDate: ev.startDate ? dayjs(ev.startDate) : null,
            endDate: ev.endDate ? dayjs(ev.endDate) : null,
        });
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
        form.setFieldsValue({ status: "UPCOMING", isClosed: false, pointsAwarded: 0 });
    };

    const save = async () => {
        const v = await form.validateFields();
        const body = {
            ...v,
            startDate: v.startDate ? v.startDate.toISOString() : undefined,
            endDate: v.endDate ? v.endDate.toISOString() : undefined,
        };
        setSaving(true);
        try {
            if (editing._id) {
                await api.updateEvent(editing._id, body);
                message.success("Event updated");
            } else {
                await api.createEvent(body);
                message.success("Event created");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Delete a single event (row action).
    const removeOne = async (id) => {
        try {
            await api.deleteEvent(id);
            message.success("Event deleted");
            setSelectedRowKeys((keys) => keys.filter((k) => k !== id));
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    // Delete every currently selected event, after a confirmation.
    const bulkDelete = () => {
        Modal.confirm({
            title: `Delete ${selectedRowKeys.length} event${
                selectedRowKeys.length === 1 ? "" : "s"
            }?`,
            content: "This can't be undone.",
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: async () => {
                setDeleting(true);
                try {
                    const r = await api.bulkDeleteEvents(selectedRowKeys);
                    message.success(`Deleted ${r.deleted} event(s)`);
                    setSelectedRowKeys([]);
                    load();
                } catch (e) {
                    message.error(e.message);
                } finally {
                    setDeleting(false);
                }
            },
        });
    };

    // Open the attendees panel with names populated (the list rows only carry
    // raw ObjectIds, which is why it used to show ids).
    const openAttendees = async (ev) => {
        setAttendees({ _id: ev._id, title: ev.title, applicants: [], participants: [] });
        try {
            const data = await api.getEventAttendees(ev._id);
            setAttendees(data);
            // The panel renders below the events table — bring it into view.
            setTimeout(
                () =>
                    document
                        .getElementById("attendees-panel")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                100
            );
        } catch (e) {
            message.error(e.message);
        }
    };

    // Move an accepted participant back to the applicants list.
    const moveToApplicants = async (eventId, userId) => {
        try {
            await api.unacceptParticipant(eventId, userId);
            message.success("Moved back to applicants");
            const data = await api.getEventAttendees(eventId);
            setAttendees(data);
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    // Explain in plain English why a certificate batch sent nothing.
    const CERT_REASONS = {
        mail_not_configured:
            "EMAIL_USER / EMAIL_PASS are not set on the server — no mail can be sent.",
        no_present_participants:
            "Nobody is marked present for this event. Certificates only go to attendees — mark attendance first.",
        not_completed: "The event is not completed yet.",
        all_failed: "Every send attempt failed — see the Certificates page for the errors.",
    };

    const [sendingCerts, setSendingCerts] = useState(false);
    const sendCertificates = async (eventId, title) => {
        setSendingCerts(true);
        try {
            const r = await api.sendCertificates(eventId);
            if (r.sent > 0) {
                message.success(`Sent ${r.sent} certificate(s) for “${title}”`);
            } else {
                message.warning(CERT_REASONS[r.reason] || "Nothing was sent");
            }
        } catch (e) {
            message.error(e.message);
        } finally {
            setSendingCerts(false);
        }
    };

    // Persist a per-participant note. Skips the request when nothing changed so
    // simply tabbing through the table doesn't spam the API.
    const saveNote = async (eventId, userId, note, previous) => {
        if ((note || "").trim() === (previous || "").trim()) return;
        try {
            await api.setParticipantNote(eventId, userId, note);
            setAttendees((prev) =>
                prev
                    ? {
                          ...prev,
                          participants: (prev.participants || []).map((p) =>
                              idOf(p) === String(userId) ? { ...p, note: note.trim() } : p
                          ),
                      }
                    : prev
            );
            message.success("Note saved");
        } catch (e) {
            message.error(e.message);
        }
    };

    // Mark/unmark a participant as a team lead for THIS event only.
    const toggleLead = async (eventId, userId, lead) => {
        try {
            await api.setEventLead(eventId, userId, lead);
            message.success(lead ? "Set as event team lead" : "Removed as team lead");
            const data = await api.getEventAttendees(eventId);
            setAttendees(data);
        } catch (e) {
            message.error(e.message);
        }
    };

    const decide = async (eventId, userId, accept) => {
        try {
            if (accept) {
                await api.acceptApplicant(eventId, userId);
            } else {
                await api.rejectApplicant(eventId, userId);
            }
            message.success(accept ? "Accepted" : "Rejected");
            // Re-fetch the populated attendees so names (not ids) stay visible,
            // and refresh the table so the capacity/pending counts update.
            const data = await api.getEventAttendees(eventId);
            setAttendees(data);
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Event",
            key: "title",
            render: (_, r) => (
                <Space>
                    <Avatar shape="square" size={44} src={r.image} icon={<CalendarOutlined />} />
                    <div>
                        <div style={{ fontWeight: 600 }}>{r.title}</div>
                        {r.category && (
                            <div className="admin-cell-sub">{r.category}</div>
                        )}
                    </div>
                </Space>
            ),
        },
        { title: "Location", dataIndex: "location", key: "location", render: (l) => l || "—" },
        {
            title: "Schedule",
            key: "schedule",
            render: (_, r) => (
                <span className="admin-cell-sub" style={{ color: "#475569" }}>
                    {fmtRange(r.startDate, r.endDate)}
                </span>
            ),
        },
        {
            title: "Points",
            dataIndex: "pointsAwarded",
            key: "pointsAwarded",
            width: 90,
            render: (p) => (
                <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                    {p || 0} pts
                </Tag>
            ),
        },
        {
            title: "Capacity",
            key: "capacity",
            render: (_, r) => {
                const parts = (r.participants || []).length;
                const pending = (r.applicants || []).length;
                return (
                    <Space>
                        <span>
                            {parts} / {r.maxParticipants ?? "∞"}
                        </span>
                        {pending > 0 && (
                            <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                                +{pending} Pending
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "Status",
            key: "status",
            render: (_, r) => (
                <Space size={6}>
                    <Tag color={statusColor(r.status)} style={{ borderRadius: 12, paddingInline: 10 }}>
                        {r.status || "—"}
                    </Tag>
                    <Tooltip title={r.isClosed ? "Closed" : "Open"}>
                        {r.isClosed ? (
                            <LockFilled style={{ color: "#ef4444" }} />
                        ) : (
                            <UnlockOutlined style={{ color: "#16a34a" }} />
                        )}
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 200,
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(r)}>
                        Edit
                    </Button>
                    <Badge count={(r.applicants || []).length} size="small">
                        <Button
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={() => openAttendees(r)}
                        >
                            Attendees
                        </Button>
                    </Badge>
                    <Popconfirm
                        title="Delete this event?"
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeOne(r._id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Events</h1>
                <Space>
                    {selectedRowKeys.length > 0 && (
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleting}
                            onClick={bulkDelete}
                        >
                            Delete selected ({selectedRowKeys.length})
                        </Button>
                    )}
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        New event
                    </Button>
                </Space>
            </div>

            <Space wrap style={{ marginBottom: 16 }}>
                <RangePicker onChange={setFRange} />
                <Select
                    allowClear
                    placeholder="Category"
                    style={{ width: 180 }}
                    value={fCategory}
                    options={categoryOptions}
                    onChange={setFCategory}
                />
                <Select
                    allowClear
                    placeholder="Status"
                    style={{ width: 160 }}
                    value={fStatus}
                    options={STATUS_OPTIONS}
                    onChange={setFStatus}
                />
            </Space>

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={filtered}
                scroll={{ x: "max-content" }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
            />

            {/* Create / edit */}
            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit event" : "New event"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                width={640}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Space style={{ display: "flex" }} align="start">
                        <Form.Item name="location" label="Location">
                            <Input />
                        </Form.Item>
                        <Form.Item name="category" label="Category">
                            <Select
                                allowClear
                                showSearch
                                placeholder="Choose"
                                style={{ width: 160 }}
                                options={CATEGORY_OPTIONS}
                            />
                        </Form.Item>
                        <Form.Item name="dressCode" label="Dress code">
                            <AutoComplete
                                allowClear
                                placeholder="Choose or type"
                                style={{ width: 180 }}
                                options={DRESS_CODE_OPTIONS}
                                filterOption={(input, option) =>
                                    (option?.value ?? "")
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: "flex" }} align="start">
                        <Form.Item name="maxParticipants" label="Max participants">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item
                            name="pointsAwarded"
                            label="Points awarded"
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="status" label="Status">
                            <Select style={{ width: 150 }} options={STATUS_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="isClosed" label="Closed" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: "flex" }} align="start">
                        <Form.Item name="startDate" label="Start date">
                            <DatePicker showTime />
                        </Form.Item>
                        <Form.Item name="endDate" label="End date">
                            <DatePicker showTime />
                        </Form.Item>
                    </Space>
                    <Form.Item name="languages" label="Languages">
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Choose languages"
                            options={LANGUAGE_OPTIONS}
                        />
                    </Form.Item>
                    <Form.Item name="image" label="Image">
                        <ImageUpload />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Attendees — full-width panel below the table, so there's room for
                contact details, attendance, lead status and per-person notes. */}
            {attendees && (
                <Card
                    id="attendees-panel"
                    style={{ marginTop: 24 }}
                    variant="borderless"
                    title={
                        <Space>
                            <span>Attendees — {attendees.title}</span>
                            <Tag color="green">
                                {(attendees.participants || []).filter((p) => p.present).length}{" "}
                                present
                            </Tag>
                            <Tag>
                                {(attendees.participants || []).length} accepted
                            </Tag>
                        </Space>
                    }
                    extra={
                        <Space>
                            <Popconfirm
                                title="Send participation certificates?"
                                description="Emails the certificate to everyone marked present. Safe to run again."
                                okText="Send"
                                onConfirm={() =>
                                    sendCertificates(attendees._id, attendees.title)
                                }
                            >
                                <Button type="primary" ghost loading={sendingCerts}>
                                    Send certificates
                                </Button>
                            </Popconfirm>
                            <Button onClick={() => setAttendees(null)}>Close</Button>
                        </Space>
                    }
                >
                    <h4>Applicants ({(attendees.applicants || []).length})</h4>
                    {(attendees.applicants || []).length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No pending applicants"
                        />
                    ) : (
                        <Table
                            rowKey={(r) => idOf(r)}
                            size="small"
                            pagination={false}
                            dataSource={attendees.applicants}
                            scroll={{ x: "max-content" }}
                            columns={[
                                {
                                    title: "Volunteer",
                                    key: "who",
                                    render: (_, a) => (
                                        <Space>
                                            <Avatar size="small" src={a.avatar}>
                                                {(a.name || "?")[0]}
                                            </Avatar>
                                            <span>{a.name || idOf(a)}</span>
                                        </Space>
                                    ),
                                },
                                { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "—" },
                                { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => v || "—" },
                                { title: "Points", dataIndex: "points", key: "points", width: 90, render: (v) => v ?? 0 },
                                {
                                    title: "",
                                    key: "actions",
                                    width: 190,
                                    align: "right",
                                    render: (_, a) => (
                                        <Space>
                                            <Button size="small" type="primary" onClick={() => decide(attendees._id, idOf(a), true)}>
                                                Accept
                                            </Button>
                                            <Button size="small" danger onClick={() => decide(attendees._id, idOf(a), false)}>
                                                Reject
                                            </Button>
                                        </Space>
                                    ),
                                },
                            ]}
                        />
                    )}

                    <h4 style={{ marginTop: 24 }}>
                        Participants ({(attendees.participants || []).length})
                    </h4>
                    <Table
                        rowKey={(r) => idOf(r)}
                        size="small"
                        dataSource={attendees.participants || []}
                        locale={{ emptyText: "No participants yet" }}
                        scroll={{ x: "max-content" }}
                        pagination={{ pageSize: 25, showTotal: (t) => `${t} participants` }}
                        columns={[
                            {
                                title: "Volunteer",
                                key: "who",
                                fixed: "left",
                                render: (_, p) => (
                                    <Space>
                                        <Avatar size="small" src={p.avatar}>
                                            {(p.name || "?")[0]}
                                        </Avatar>
                                        <span>{p.name || idOf(p)}</span>
                                        {p.isLead && <Tag color="gold">★ lead</Tag>}
                                    </Space>
                                ),
                            },
                            { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "—" },
                            { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => v || "—" },
                            {
                                title: "Languages",
                                key: "languages",
                                render: (_, p) =>
                                    (p.languages || []).length
                                        ? p.languages.map((l) => <Tag key={l}>{l}</Tag>)
                                        : "—",
                            },
                            {
                                title: "Points",
                                dataIndex: "points",
                                key: "points",
                                width: 90,
                                sorter: (a, b) => (a.points || 0) - (b.points || 0),
                                render: (v) => v ?? 0,
                            },
                            {
                                title: "Events",
                                dataIndex: "attendedEventsCount",
                                key: "attended",
                                width: 90,
                                render: (v) => v ?? 0,
                            },
                            {
                                title: "Attendance",
                                key: "attendance",
                                width: 190,
                                filters: [
                                    { text: "Present", value: true },
                                    { text: "Absent", value: false },
                                ],
                                onFilter: (val, p) => !!p.present === val,
                                render: (_, p) =>
                                    p.present ? (
                                        p.method === "manual" ? (
                                            <Tag color="blue">
                                                Present · marked{p.markedBy ? ` by ${p.markedBy}` : ""}
                                            </Tag>
                                        ) : (
                                            <Tag color="green">Present · QR</Tag>
                                        )
                                    ) : (
                                        <Tag>Absent</Tag>
                                    ),
                            },
                            {
                                title: "Note",
                                key: "note",
                                width: 260,
                                render: (_, p) => (
                                    <Input.TextArea
                                        defaultValue={p.note || ""}
                                        placeholder="Add a note…"
                                        autoSize={{ minRows: 1, maxRows: 3 }}
                                        maxLength={500}
                                        onBlur={(e) =>
                                            saveNote(attendees._id, idOf(p), e.target.value, p.note || "")
                                        }
                                    />
                                ),
                            },
                            {
                                title: "",
                                key: "actions",
                                width: 220,
                                align: "right",
                                render: (_, p) => (
                                    <Space>
                                        <Button
                                            size="small"
                                            type={p.isLead ? "default" : "primary"}
                                            ghost={!p.isLead}
                                            onClick={() => toggleLead(attendees._id, idOf(p), !p.isLead)}
                                        >
                                            {p.isLead ? "Remove lead" : "Make lead"}
                                        </Button>
                                        <Popconfirm
                                            title="Move this attendee back to applicants?"
                                            okText="Move"
                                            onConfirm={() => moveToApplicants(attendees._id, idOf(p))}
                                        >
                                            <Button size="small" danger>
                                                Move back
                                            </Button>
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </Card>
            )}
        </div>
    );
};

export default EventsPage;
